import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { PasswordService } from '@renovationman/rm-uaa-api-client-ts-ng20';
import { ORIGIN_URL_QUERY_PARAM_KEY } from 'libs/auth-lib/src/public-api';
import { throwError } from 'rxjs';
import { catchError, finalize, first } from 'rxjs/operators';

import {
  APP_TYPE_QUERY_PARAM_KEY,
  FIRST_LOGIN_QUERY_PARAM_KEY,
  RESET_CODE_QUERY_PARAM_KEY,
} from '~constants';

enum FormControls {
  NewPassword = 'newPassword',
  NewPasswordConfirm = 'newPasswordConfirm',
}

interface IFormValue {
  [FormControls.NewPassword]: string;
  [FormControls.NewPasswordConfirm]: string;
}

@Component({
  templateUrl: './reset-password.page.html',
  standalone: false,
})
export class ResetPasswordPage implements OnInit {
  formControls = FormControls;
  firstLogin = false;
  submitted = false;
  submitting = false;
  formSent = false;
  originUrl: string;
  appType: string;
  private resetCode: string;

  minimumLength = 6;

  formGroup = new FormGroup(
    {
      [FormControls.NewPassword]: new FormControl<string | null>(null, [
        Validators.required,
        Validators.minLength(this.minimumLength),
      ]),
      [FormControls.NewPasswordConfirm]: new FormControl<string | null>(
        null,
        Validators.required
      ),
    },
    { validators: this.checkPasswords }
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private passwordService: PasswordService
  ) {}

  checkPasswords(group: UntypedFormGroup) {
    const newPassword = group.get(FormControls.NewPassword).value;
    const newPasswordConfirm = group.get(FormControls.NewPasswordConfirm).value;

    return newPasswordConfirm && newPassword === newPasswordConfirm
      ? null
      : { notSame: true };
  }

  get passwordLengthError() {
    return (
      this.formGroup.get(FormControls.NewPassword).touched &&
      this.formGroup.get(FormControls.NewPassword).hasError('minlength')
    );
  }

  get passwordAreNotTheSame() {
    return (
      this.formGroup.get(FormControls.NewPasswordConfirm).touched &&
      this.formGroup.hasError('notSame')
    );
  }

  get contentTranslateKey() {
    if (this.formSent) {
      return this.firstLogin ? 'FIRST_CONNECTION_SUCCESS' : 'NORMAL_SUCCESS';
    }
    return this.firstLogin ? 'FIRST_CONNECTION' : 'NORMAL';
  }

  get iconFileName() {
    if (this.formSent) {
      return 'reset-password-success';
    }
    return this.firstLogin ? 'first-connection' : 'reset-password';
  }

  /**
   * Ce que le lien « me connecter » emporte vers le portail — et rien d'autre.
   *
   * <strong>Pourquoi ce n'est plus un `queryParamsHandling="merge"`.</strong> Le `merge` recopiait la
   * query entière de cette page, donc le `reset-code` <em>déjà consommé</em>, qui allait se graver
   * dans l'historique du navigateur sur une URL où il n'a plus rien à faire. Les deux seuls
   * paramètres utiles au portail sont énumérés ici : `type` pour le gabarit de sa page, `originUrl`
   * pour le lien profond.
   */
  get queryParams() {
    const params = {
      ...(this.appType ? { [APP_TYPE_QUERY_PARAM_KEY]: this.appType } : {}),
      ...(this.originUrl
        ? { [ORIGIN_URL_QUERY_PARAM_KEY]: this.originUrl }
        : {}),
    };
    return Object.keys(params).length ? params : null;
  }

  ngOnInit() {
    this.route.data
      .pipe(first())
      .subscribe((data) => (this.firstLogin = !!data.firstLogin));
    this.route.queryParamMap.pipe(first()).subscribe((queryParamMap) => {
      this.resetCode = queryParamMap.get(RESET_CODE_QUERY_PARAM_KEY);
      this.originUrl = queryParamMap.get(ORIGIN_URL_QUERY_PARAM_KEY);
      this.appType = queryParamMap.get(APP_TYPE_QUERY_PARAM_KEY);
    });
  }

  submit() {
    this.submitted = true;
    this.formGroup.markAllAsTouched();
    if (!this.formGroup.valid) {
      return;
    }
    const formValues: IFormValue = this.formGroup.value as IFormValue;
    this.submitting = false;
    this.formSent = true;
    this.passwordService
      .updateForgottenPassword({
        token: this.resetCode,
        newPassword: formValues.newPassword,
        newPasswordConfirmation: formValues.newPasswordConfirm,
        firstPassword: this.firstLogin,
      })
      .pipe(
        catchError((error) => {
          this.router.navigate(['expired-link'], {
            queryParams: {
              [RESET_CODE_QUERY_PARAM_KEY]: undefined,
              [FIRST_LOGIN_QUERY_PARAM_KEY]: this.firstLogin || undefined,
            },
            queryParamsHandling: 'merge',
          });
          return throwError(error);
        }),
        finalize(() => (this.submitting = false))
      )
      .subscribe();
  }
}
